import { Injectable } from '@angular/core';
import { StorageService, StorageTypes } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, defaultIfEmpty, forkJoin, map, of, take } from 'rxjs';
import { SearchType } from '../models/search-type';
import { AwbLookUpService } from './awb-look-up.service';
import { SummaryService } from './summary/summary.service';

@Injectable({
    providedIn: 'root',
})
export class FlightService {
    private _flightNumber = new BehaviorSubject<string | null>(null);
    private _flightDate = new BehaviorSubject<string | null>(null);
    private _flightDestination = new BehaviorSubject<string | null>(null);

    constructor(
        private storageService: StorageService,
        private summaryService: SummaryService,
        private awbLookUpService: AwbLookUpService
    ) {
        const flight_date: string | null = this.storageService.getElement('flight_date', StorageTypes.SESSION);
        if (flight_date) {
            this.setFlightDate(flight_date);
        }
        const flight_number: string | null = this.storageService.getElement('flight_number', StorageTypes.SESSION);
        if (flight_number) {
            this.setFlightNumber(flight_number);
        }
        const flight_destination: string | null = this.storageService.getElement(
            'flight_destination',
            StorageTypes.SESSION
        );
        if (flight_destination) {
            this.setFlightDestination(flight_destination);
        }
    }

    setFlightNumber(flightNumber: string | null): void {
        this._flightNumber.next(flightNumber);
        this.saveToStorage('flight_number', flightNumber);
    }

    get instant_flight_number(): string {
        return this._flightNumber.value || '';
    }

    get flightNumber$(): Observable<string | null> {
        return this._flightNumber.asObservable();
    }

    setFlightDate(flightDate: string | null): void {
        this._flightDate.next(flightDate);
        this.saveToStorage('flight_date', flightDate);
    }

    get instant_flight_date(): string {
        return this._flightDate.value || '';
    }

    get flightDate$(): Observable<string | null> {
        return this._flightDate.asObservable();
    }

    setFlightDestination(flightDestination: string): void {
        this._flightDestination.next(flightDestination);
        this.saveToStorage('flight_destination', flightDestination);
    }

    get instant_flight_destination(): string {
        return this._flightDestination.value || '';
    }

    private saveToStorage(key: string, value: string | null): void {
        this.storageService.saveElement(key, value, StorageTypes.SESSION);
    }

    getFlightNumberBySearchTypeAndUniqueId$(uniqueId?: string): Observable<string> {
        if (this.summaryService.getSearchType() === SearchType.awb) {
            if (uniqueId) {
                return this.awbLookUpService
                    .getChargeByUniqueId(uniqueId)
                    .pipe(map((charge) => charge?.flightNumber || ''));
            }

            return of('');
        }

        return this.flightNumber$.pipe(map((flightNumber) => flightNumber || ''));
    }

    getFlightDateBySearchTypeAndUniqueId$(uniqueId?: string): Observable<string> {
        if (this.summaryService.getSearchType() === SearchType.awb) {
            if (uniqueId) {
                return this.awbLookUpService
                    .getChargeByUniqueId(uniqueId)
                    .pipe(map((charge) => charge?.flightDate || ''));
            }

            return of(this.awbLookUpService.getFlightDate());
        }

        return this.flightDate$.pipe(map((flightDate) => flightDate || ''));
    }

    getFlightNumberAndFlightDateBySearchTypeAndUniqueId$(uniqueId?: string): Observable<{
        flightNumber: string;
        flightDate: string;
    }> {
        return forkJoin([
            this.getFlightNumberBySearchTypeAndUniqueId$(uniqueId).pipe(take(1)),
            this.getFlightDateBySearchTypeAndUniqueId$(uniqueId).pipe(take(1)),
        ]).pipe(
            take(1),
            map((flightInformation) => {
                return {
                    flightNumber: flightInformation[0] || '',
                    flightDate: flightInformation[1] || '',
                };
            })
        );
    }

    getFlightNumberAndFlightDateBySearchTypeAndFlightInfo$(
        flightNumber?: string,
        flightDate?: string
    ): Observable<{
        flightNumber: string;
        flightDate: string;
    }> {
        if (this.summaryService.getSearchType() === SearchType.awb) {
            return this.awbLookUpService
                .getFlightByFlightNumberAndFlightDate$(flightNumber || '', flightDate || '')
                .pipe(
                    map((flight) => ({
                        flightNumber: flight?.flightNumber || '',
                        flightDate: flight?.flightDate || '',
                    }))
                );
        }

        return forkJoin([this.flightNumber$, this.flightDate$]).pipe(
            take(1),
            defaultIfEmpty([]),
            map((flightInformation) => ({
                flightNumber: flightInformation[0] || '',
                flightDate: flightInformation[1] || '',
            }))
        );
    }
}
